import React, { useState, useEffect } from 'react';

export default function FloatingButtons() {
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [showSocialMenu, setShowSocialMenu] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollBtn(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleSocialMenu = () => {
    setShowSocialMenu(!showSocialMenu);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3 pointer-events-none">
      {/* Global Styles for animations */}
      <style jsx>{`
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.8);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes slideInUpDelay1 {
          0% {
            opacity: 0;
            transform: translateY(20px) scale(0.8);
          }
          40% {
            opacity: 0;
            transform: translateY(20px) scale(0.8);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes slideInUpDelay2 {
          0% {
            opacity: 0;
            transform: translateY(20px) scale(0.8);
          }
          60% {
            opacity: 0;
            transform: translateY(20px) scale(0.8);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes rotatePlus {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(45deg);
          }
        }

        @keyframes pulse {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.7);
          }
          50% {
            box-shadow: 0 0 0 10px rgba(255, 193, 7, 0);
          }
        }

        @keyframes blink-dot {
          0%, 49% {
            opacity: 1;
          }
          50%, 100% {
            opacity: 0.3;
          }
        }

        .animate-slide-in-up {
          animation: slideInUp 0.5s ease-out forwards;
        }

        .animate-slide-in-up-delay-1 {
          animation: slideInUpDelay1 0.8s ease-out forwards;
        }

        .animate-slide-in-up-delay-2 {
          animation: slideInUpDelay2 0.8s ease-out forwards;
        }

        .animate-rotate-plus {
          animation: rotatePlus 0.4s ease-out forwards;
        }

        .animate-pulse-btn {
          animation: pulse 2s infinite;
        }

        .animate-blink-dot {
          animation: blink-dot 1.2s ease-in-out infinite;
        }

        .social-btn-whatsapp {
          transition: all 0.3s ease;
        }

        .social-btn-whatsapp:hover {
          transform: scale(1.15) translateX(-5px);
          box-shadow: 0 15px 30px rgba(34, 197, 94, 0.4);
        }

        .social-btn-linkedin {
          transition: all 0.3s ease;
        }

        .social-btn-linkedin:hover {
          transform: scale(1.15) translateX(-5px);
          box-shadow: 0 15px 30px rgba(37, 99, 235, 0.4);
        }

        .social-btn-email {
          transition: all 0.3s ease;
        }

        .social-btn-email:hover {
          transform: scale(1.15) translateX(-5px);
          box-shadow: 0 15px 30px rgba(245, 158, 11, 0.4);
        }

        .btn-label {
          white-space: nowrap;
          display: flex;
          align-items: center;
          gap: 8px;
        }
      `}</style>

      {/* Backdrop */}
      {showSocialMenu && (
        <div
          className="fixed inset-0 z-40 pointer-events-auto"
          onClick={() => setShowSocialMenu(false)}
        />
      )}

      {/* Social Menu Container */}
      {showSocialMenu && (
        <div className="flex flex-col items-end gap-4 z-50 relative pointer-events-auto">
          {/* WhatsApp */}
          <div className="animate-slide-in-up-delay-1 flex items-center gap-3">
            <div className="bg-white px-4 py-2 rounded-lg shadow-lg text-green-600 font-bold text-sm">
              Talk to Us
            </div>
            <a
              href="https://wa.me/919522950225?text=Hello%20Garud%20Stacks"
              target="_blank"
              rel="noopener noreferrer"
              className="social-btn-whatsapp w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 text-white rounded-full shadow-xl flex items-center justify-center cursor-pointer group relative overflow-hidden"
            >
              {/* Animated background gradient */}
              <div className="absolute inset-0 bg-gradient-to-r from-green-300 to-green-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              {/* WhatsApp Icon */}
              <svg className="w-8 h-8 relative z-10" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 00-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </a>
          </div>

          {/* Email */}
          <div className="animate-slide-in-up-delay-2 flex items-center gap-3">
            <div className="bg-white px-4 py-2 rounded-lg shadow-lg text-amber-600 font-bold text-sm">
              Email Us
            </div>
            <a
              href="mailto:contact@garudstacks.tech"
              className="social-btn-email w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 text-white rounded-full shadow-xl flex items-center justify-center cursor-pointer group relative overflow-hidden"
            >
              {/* Animated background gradient */}
              <div className="absolute inset-0 bg-gradient-to-r from-amber-300 to-amber-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              {/* Email Icon */}
              <svg className="w-8 h-8 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </a>
          </div>
        </div>
      )}

      {/* Plus Button */}
      <button
        onClick={toggleSocialMenu}
        className={`w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 text-black rounded-full shadow-2xl flex items-center justify-center hover:shadow-amber-500/50 pointer-events-auto cursor-pointer font-bold text-3xl transition-all duration-300 group relative ${
          showSocialMenu ? 'animate-rotate-plus' : ''
        } ${!showSocialMenu ? 'animate-pulse-btn' : ''}`}
      >
        {/* Shimmer effect on hover */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-40 transition-opacity duration-300 bg-gradient-to-r from-transparent via-white to-transparent rounded-full"></div>
        
        {/* Notification Badge - Simple Red Dot with Blink */}
        <div className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 rounded-full shadow-lg border border-white z-20 pointer-events-none animate-blink-dot"></div>
        
        {/* Plus icon with rotation animation */}
        <span className={`relative z-10 transition-transform duration-300 ${showSocialMenu ? 'rotate-45' : ''}`}>
          +
        </span>
      </button>

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-all duration-300 pointer-events-auto cursor-pointer group relative overflow-hidden ${
          showScrollBtn
            ? 'opacity-100 bg-gradient-to-br from-gray-800 to-gray-900 text-white hover:bg-gradient-to-br hover:from-amber-500 hover:to-amber-600 hover:text-black'
            : 'opacity-0 pointer-events-none bg-gray-900 text-white'
        }`}
      >
        {/* Shimmer effect */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300 bg-gradient-to-r from-transparent via-white to-transparent"></div>
        
        <svg
          className="w-7 h-7 relative z-10"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 10l7-7m0 0l7 7m-7-7v18"
          />
        </svg>
      </button>
    </div>
  );
}
